import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  useToast,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Text,
  Stack,
} from "@chakra-ui/react";
import { BASE_URL } from "../../../../../Globalapi/Globalapi";

const EditFineModal = ({ fineId }) => {
  const toast = useToast();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [fineDetails, setFineDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fineIntervals, setFineIntervals] = useState([]);
  const schoolId = sessionStorage.getItem("school_id");

  // Function to fetch fine details from the API
  const fetchFineDetails = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${BASE_URL}/admin/library/fine/view_fine.php?id=${fineId}&school_id=${schoolId}`
      );
      const data = await response.json();

      if (data.success) {
        setFineDetails(data.data);
        if (data.data.fine_type === "interval" && data.data.fine_intervals) {
          // Parse the fine_intervals string into an array of objects
          const intervals = JSON.parse(data.data.fine_intervals);
          setFineIntervals(intervals);
        }
      } else {
        setError(data.message);
      }
    } catch (error) {
      setError("Failed to fetch fine details");
    } finally {
      setLoading(false);
    }
  };

  // Open the modal and fetch fine details
  const handleEditClick = () => {
    fetchFineDetails();
    onOpen();
  };

  // Handle changes to intervals
  const handleIntervalChange = (index, field, value) => {
    const updatedIntervals = [...fineIntervals];
    updatedIntervals[index][field] = value;
    setFineIntervals(updatedIntervals);
  };

  // Save changes to fine details
  const handleSaveChanges = async () => {
    setLoading(true);
    const updatedFineDetails = {
      id: fineId,
      school_master_id: schoolId,
      fine_type: fineDetails.fine_type,
      fixed_fine_amount:
        fineDetails.fine_type === "fixed"
          ? fineDetails.fixed_fine_amount
          : null,
      fine_intervals:
        fineDetails.fine_type === "interval" ? fineIntervals : null, // Pass as an array
    };

    try {
      const response = await fetch(
        `${BASE_URL}/admin/library/fine/edit_fine.php`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatedFineDetails),
        }
      );

      const data = await response.json();

      if (data.success) {
        onClose();
        console.log("Toast triggered");
        toast({
          title: "Success",
          description: "Fine updated successfully!",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        setError(data.message);
      }
    } catch (error) {
      setError("Failed to update fine");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Edit Button */}
      <Button size="sm" colorScheme="blue" onClick={handleEditClick}>
        Edit
      </Button>

      {/* Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Fine Details</ModalHeader>
          <ModalBody>
            {loading ? (
              <Text>Loading...</Text>
            ) : error ? (
              <Text color="red.500">{error}</Text>
            ) : fineDetails ? (
              <Stack spacing={4}>
                <FormControl>
                  {/* Display Fine Type as text (non-editable) */}

                  {/* Handle fixed fine type */}
                  {fineDetails.fine_type === "fixed" ? (
                    <>
                      <FormLabel>Fine Amount</FormLabel>
                      <Input value={fineDetails.fixed_fine_amount} isReadOnly />
                    </>
                  ) : fineDetails.fine_type === "interval" ? (
                    <>
                      {/* Handle interval fine type */}
                      <FormLabel>Intervals</FormLabel>
                      {fineIntervals.length > 0 ? (
                        fineIntervals.map((interval, index) => (
                          <Stack
                            key={index}
                            spacing={3}
                            borderWidth="1px"
                            borderRadius="md"
                            p={4}
                          >
                            <FormControl>
                              <FormLabel>Days</FormLabel>
                              <Input
                                type="number"
                                value={interval.days}
                                onChange={(e) =>
                                  handleIntervalChange(
                                    index,
                                    "days",
                                    e.target.value
                                  )
                                }
                                placeholder="Enter number of days"
                              />
                            </FormControl>
                            <FormControl>
                              <FormLabel>Fine Amount</FormLabel>
                              <Input
                                type="number"
                                value={interval.fine_amount}
                                onChange={(e) =>
                                  handleIntervalChange(
                                    index,
                                    "fine_amount",
                                    e.target.value
                                  )
                                }
                                placeholder="Enter fine amount"
                              />
                            </FormControl>
                          </Stack>
                        ))
                      ) : (
                        <Text>No intervals set</Text>
                      )}
                    </>
                  ) : null}
                </FormControl>
              </Stack>
            ) : (
              <Text>No fine details available</Text>
            )}
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
            <Button
              colorScheme="blue"
              ml={3}
              onClick={handleSaveChanges}
              isLoading={loading}
              isDisabled={loading}
            >
              Save Changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default EditFineModal;
